import React, {
  TextareaHTMLAttributes,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import { AppTypography } from '../appUi';
import AppFieldLine from './AppFieldLine';
import AppReadonlyField from './AppReadonlyField';
import AppInputLabel from './AppInputLabel';
import { useAutoResizeTextarea } from '../../../hooks/useAutoResizeTextarea';
import styles from './AppFormTextarea.module.css';

interface AppFormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  labelAction?: React.ReactNode;
  error?: string;
  required?: boolean;
  containerClassName?: string;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const AppFormTextarea = forwardRef<HTMLTextAreaElement, AppFormTextareaProps>(({
  label,
  labelAction,
  error,
  required = false,
  id,
  containerClassName = '',
  className = '',
  autoResize = true,
  minRows,
  maxRows,
  rows,
  value,
  onChange,
  readOnly,
  ...rest
}, ref) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const resolvedMinRows = minRows ?? rows ?? 1;
  const resolvedMaxRows = maxRows ?? (readOnly ? Number.POSITIVE_INFINITY : 12);

  const setRef = useCallback((node: HTMLTextAreaElement | null) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  const adjustHeight = useAutoResizeTextarea(internalRef, {
    enabled: autoResize,
    minRows: resolvedMinRows,
    maxRows: resolvedMaxRows,
    value: typeof value === 'string' ? value : undefined,
  });

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);
    if (autoResize) {
      requestAnimationFrame(() => adjustHeight());
    }
  }, [adjustHeight, autoResize, onChange]);

  const textareaClassName = [
    styles.textarea,
    autoResize ? styles.textareaAutoResize : '',
    className,
  ].filter(Boolean).join(' ');
  const showRequired = required && !readOnly;

  if (readOnly) {
    return (
      <AppReadonlyField
        id={id}
        label={label}
        value={typeof value === 'string' ? value : ''}
        containerClassName={containerClassName}
      />
    );
  }

  return (
    <div className={`${styles.container} ${containerClassName}`}>
      {label && (
        <div className={styles.labelRow}>
          <AppInputLabel htmlFor={id} required={showRequired}>{label}</AppInputLabel>
          {labelAction}
        </div>
      )}
      <textarea
        ref={setRef}
        id={id}
        className={textareaClassName}
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        required={showRequired}
        rows={autoResize ? resolvedMinRows : rows}
        aria-invalid={Boolean(error)}
        {...rest}
      />
      <AppFieldLine error={Boolean(error)} />
      {error ? (
        <AppTypography variant="caption" className={styles.errorText}>
          {error}
        </AppTypography>
      ) : null}
    </div>
  );
});

AppFormTextarea.displayName = 'AppFormTextarea';

export default AppFormTextarea;
